import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Body2, Button, Dialog, InfoBox, Input, InputPassword, Label } from "@activecollab/components";
import { Field, Form, Formik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";
import { useFocusInvalidField } from "~/components/Hooks/useFocusInvalidField";
import { useUpdatePaymentGateway } from "~/repository/usePaymentGateways";

export const PayPalFormDialog = ({ integration, open, onClose }) => {
  const { t } = useTranslation();
  const [showErrors, setShowErrors] = useState([]);
  const { fieldRef, focusFirstInvalidField } = useFocusInvalidField();

  const { mutate: updatePaypal } = useUpdatePaymentGateway();

  const validationSchema = yup.object().shape({
    name: yup.string().trim().required(),
    password: yup.string().trim().required(),
    secret: yup.string().trim().required(),
  });

  const handleSubmit = useCallback(
    ({ name, password, secret }) => {
      if (integration) {
        updatePaypal(
          {
            paypal: {
              type: "PaypalExpressCheckoutGateway",
              is_enabled: true,
              api_username: name,
              api_password: password,
              api_signature: secret,
            },
          },
          {
            onSuccess: onClose,
          }
        );
      }
    },
    [integration, onClose, updatePaypal]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
    >
      <Formik
        initialValues={{
          name: "",
          password: "",
          secret: "",
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnMount
      >
        {({ isSubmitting, errors, setFieldValue, handleSubmit }) => {
          const handleFormSubmit = (e) => {
            e.preventDefault();

            handleSubmit();
            if (Object.keys(errors)?.length) {
              setShowErrors(Object.keys(errors));
              return focusFirstInvalidField(errors, false, ["name", "password", "secret"]);
            }
          };
          return (
            <Form
              role="form"
              onSubmit={handleFormSubmit}
              autoComplete="off"
              data-testid="paypal-form-dialog"
            >
              <Dialog.Title>{t("Configure Paypal")}</Dialog.Title>
              <Dialog.ContentDivider />
              <Dialog.Content className="tw-flex tw-flex-col tw-gap-4">
                <InfoBox
                  type="neutral"
                  showIcon
                >
                  <Body2>
                    {t("You can find this information by logging in to your PayPal account.")}{" "}
                    <a
                      href="https://activecollab.com/help/books/invoicing/online-payments#how-to-connect-to-paypal-express-checkout"
                      target="_blank"
                      className="tw-no-underline"
                      rel="noreferrer"
                    >
                      {t("Learn More.")}
                    </a>
                  </Body2>
                </InfoBox>
                <Field name="name">
                  {({ field, meta }) => (
                    <div className="tw-flex tw-flex-col tw-gap-1">
                      <Label
                        size="small"
                        htmlFor="name"
                      >
                        {t("API Username")}*
                      </Label>
                      <Input
                        {...field}
                        ref={(el) => {
                          if (fieldRef) {
                            fieldRef.current[field.name] = el;
                          }
                        }}
                        data-testid="paypal-username"
                        className="tw-w-full tw-max-w-[360px]"
                        type="text"
                        autoFocus
                        id="name"
                        invalid={meta.error && meta.touched && showErrors.includes(field.name)}
                        onClick={() => {
                          setShowErrors((prev) => prev.filter((err) => err !== field.name));
                        }}
                      />
                    </div>
                  )}
                </Field>
                <Field name="password">
                  {({ field, meta }) => (
                    <div className="tw-flex tw-flex-col tw-gap-1 tw-w-full tw-max-w-[360px]">
                      <Label
                        size="small"
                        htmlFor="password"
                      >
                        {t("API Password")}*
                      </Label>
                      <InputPassword
                        {...field}
                        autoComplete="new-password"
                        ref={(el) => {
                          if (fieldRef) {
                            fieldRef.current[field.name] = el;
                          }
                        }}
                        wrapperClassName="tw-w-full tw-max-w-full"
                        className="tw-w-full"
                        id="password"
                        name="password"
                        onChange={(value) => setFieldValue("password", value)}
                        invalid={meta.error && meta.touched && showErrors.includes(field.name)}
                        onClick={() => {
                          setShowErrors((prev) => prev.filter((err) => err !== field.name));
                        }}
                      />
                    </div>
                  )}
                </Field>
                <Field name="secret">
                  {({ field, meta }) => (
                    <div className="tw-flex tw-flex-col tw-gap-1 tw-w-full tw-max-w-[360px]">
                      <Label
                        size="small"
                        htmlFor="secret"
                      >
                        {t("API Signature")}*
                      </Label>
                      <InputPassword
                        {...field}
                        autoComplete="new-password"
                        ref={(el) => {
                          if (fieldRef) {
                            fieldRef.current[field.name] = el;
                          }
                        }}
                        wrapperClassName="tw-w-full tw-max-w-full"
                        className="tw-w-full"
                        id="secret"
                        name="secret"
                        onChange={(value) => setFieldValue("secret", value)}
                        invalid={meta.error && meta.touched && showErrors.includes(field.name)}
                        onClick={() => {
                          setShowErrors((prev) => prev.filter((err) => err !== field.name));
                        }}
                      />
                    </div>
                  )}
                </Field>
              </Dialog.Content>
              <Dialog.ContentDivider />
              <Dialog.Actions>
                <Button
                  type="submit"
                  className="tw-mr-3"
                  disabled={isSubmitting}
                >
                  {t("Connect")}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                >
                  {t("Cancel")}
                </Button>
              </Dialog.Actions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

PayPalFormDialog.propTypes = {
  integration: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};
